import axios from 'axios';

import type { IAssistantScript } from '../interfaces/assistantScript';
import type {
  ICreateAssistantScript,
  IUpdateAssistantScript,
} from '../interfaces/assistantScripts';
import type { PollyLanguage } from '../interfaces/polly';

let baseUrl = '';
let getToken: (() => string | null | undefined) | null = null;

export default class AssistantScriptsService {
  static configure(
    url: string,
    options?: { getToken?: () => string | null | undefined },
  ): void {
    baseUrl = url;
    if (options?.getToken) {
      getToken = options.getToken;
    }
  }

  private static authConfig(extra: any = {}): any {
    const token = getToken?.();

    if (!token) {
      return extra;
    }

    return {
      ...extra,
      headers: { ...(extra.headers || {}), Authorization: `Bearer ${token}` },
    };
  }

  static async getAssistantScripts(
    config: any = {},
  ): Promise<IAssistantScript[]> {
    const { data } = await axios.get(
      `${baseUrl}api/v2/assistant/scripts`,
      this.authConfig(config),
    );
    return data;
  }

  static async createAssistantScript(
    data: ICreateAssistantScript & { eventId?: string },
  ): Promise<void> {
    await axios.post(
      `${baseUrl}api/v2/assistant/scripts`,
      data,
      this.authConfig(),
    );
  }

  static async updateAssistantScript(
    scriptId: string,
    data: IUpdateAssistantScript & { eventId?: string },
    config: any = {},
  ): Promise<void> {
    await axios.patch(
      `${baseUrl}api/v2/assistant/scripts/${scriptId}`,
      data,
      this.authConfig(config),
    );
  }

  static async deleteAssistantScript(scriptId: string): Promise<void> {
    await axios.delete(
      `${baseUrl}api/v2/assistant/scripts/${scriptId}`,
      this.authConfig(),
    );
  }

  static async translateScript(data: {
    languages: PollyLanguage[];
    texts: string[];
  }): Promise<{ translations: Partial<Record<PollyLanguage, string[]>> }> {
    const { data: result } = await axios.post(
      `${baseUrl}api/v2/assistant/translate`,
      data,
      this.authConfig(),
    );
    const translations: Partial<Record<PollyLanguage, string[]>> = {};

    for (const entry of result) {
      translations[entry.language as PollyLanguage] = entry.texts;
    }

    return { translations };
  }
}
